import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { selectedCompanyStorage } from '../../storage/company/selected-company.util';
import { StakeholderRole } from '../../types/stakeholder.types';
import { useAppMutation } from '../use-app-mutation.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';

export const useSetSelected = () => {
  const queryClient = useQueryClient();

  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SELECTED_COMPANY,
    QueryKey.GET_NOTIFICATIONS,
    QueryKey.GET_UNREAD_NOTIFICATION_COUNT,
  );
  const setCompanyId = selectedCompanyStorage(({ setCompanyId }) => setCompanyId);

  const {
    mutate: setSelected,
    // Awaitable, for a caller that has to know the switch has landed before it navigates: the
    // AuthGuard puts somebody who just accepted a data-room invitation into that company first,
    // and sending them on beforehand renders the company they were leaving.
    mutateAsync: setSelectedAsync,
    isPending,
  } = useAppMutation([QueryKey.PATCH_SELECTED_COMPANY], {
    mutationFn: (id: string) =>
      ApiService.post<{ roleInCompany: StakeholderRole }>({
        endpoint: BackendRoute.COMPANIES,
        routePath: [id, 'select'],
      }),
    defaultErrorHandling: false,
    onSuccess: (_, companyId) => {
      setCompanyId(companyId);
      invalidateQuery();
      queryClient.resetQueries({
        predicate: (query) =>
          ![QueryKey.GET_COMPANIES, QueryKey.GET_SELECTED_COMPANY].includes(
            query.queryKey[0] as QueryKey,
          ),
      });
    },
  });
  return {
    setSelected,
    setSelectedAsync,
    isPending,
    invalidateQuery,
  };
};
